import { gql } from '@apollo/client';

// Step 1 of the parent OTP flow. See "Parent OTP auth — one mutation pair"
// in CLAUDE.md for the auth-context branching.
export const REQUEST_PARENT_CONTACT_CODE = gql`
  mutation RequestParentContactCode($destination: String!) {
    parentRequestContactCode(destination: $destination)
  }
`;

// Step 2 of the parent OTP flow. The optional `token` in the response is the
// auth-context signal — see "Parent OTP auth — one mutation pair" in CLAUDE.md.
export const VERIFY_PARENT_CONTACT = gql`
  mutation VerifyParentContact($destination: String!, $code: String!) {
    parentVerifyContact(destination: $destination, code: $code) {
      _id
      name
      emails
      phones
      token
      takeover {
        movedContacts
        retiredParent
      }
    }
  }
`;

// Rename the parent. Server cascades to every PlayerContact this parent owns.
export const PARENT_UPDATE_NAME = gql`
  mutation ParentUpdateName($value: String!) {
    parentUpdateName(value: $value) {
      _id
      name
      emails
      phones
    }
  }
`;

// Promote a verified email to position 0 (= the default).
export const PARENT_SET_DEFAULT_EMAIL = gql`
  mutation ParentSetDefaultEmail($value: String!) {
    parentSetDefaultEmail(value: $value) {
      _id
      name
      emails
      phones
    }
  }
`;

// Promote a verified phone to position 0 (= the default).
export const PARENT_SET_DEFAULT_PHONE = gql`
  mutation ParentSetDefaultPhone($value: String!) {
    parentSetDefaultPhone(value: $value) {
      _id
      name
      emails
      phones
    }
  }
`;

// Remove a verified email from the parent's list. Server only mutates the
// Parent doc — PlayerContacts that currently use this value are left as-is.
export const PARENT_REMOVE_EMAIL = gql`
  mutation ParentRemoveEmail($value: String!) {
    parentRemoveEmail(value: $value) {
      _id
      name
      emails
      phones
    }
  }
`;

// Same shape as PARENT_REMOVE_EMAIL for phone.
export const PARENT_REMOVE_PHONE = gql`
  mutation ParentRemovePhone($value: String!) {
    parentRemovePhone(value: $value) {
      _id
      name
      emails
      phones
    }
  }
`;

// Adds a co-parent contact to a player this parent already owns. The new row is
// created unclaimed, so it does NOT join this parent's own contact list.
export const PARENT_ADD_PLAYER_CONTACT = gql`
  mutation ParentAddPlayerContact($playerId: ID!, $name: String!, $email: String, $phone: String, $relationship: Relationship, $sportInvolvement: SportInvolvement) {
    parentAddPlayerContact(playerId: $playerId, name: $name, email: $email, phone: $phone, relationship: $relationship, sportInvolvement: $sportInvolvement) {
      _id
      playerId
      name
      email
      phone
      relationship
      sportInvolvement
      playerName
      companyName
      sport
    }
  }
`;

// Per-PlayerContact override of email/phone/relationship. Any field may be
// omitted; an empty string / null clears it. Normalization is server-side.
export const PARENT_UPDATE_PLAYER_CONTACT = gql`
  mutation ParentUpdatePlayerContact($_id: ID!, $email: String, $phone: String, $relationship: Relationship, $sportInvolvement: SportInvolvement) {
    parentUpdatePlayerContact(_id: $_id, email: $email, phone: $phone, relationship: $relationship, sportInvolvement: $sportInvolvement) {
      _id
      playerId
      name
      email
      phone
      relationship
      sportInvolvement
      playerName
      companyName
      sport
    }
  }
`;

// Copy the parent's current defaults onto a PlayerContact.
export const PARENT_APPLY_DEFAULTS_TO_PLAYER_CONTACT = gql`
  mutation ParentApplyDefaultsToPlayerContact($_id: ID!) {
    parentApplyDefaultsToPlayerContact(_id: $_id) {
      _id
      playerId
      name
      email
      phone
      relationship
      sportInvolvement
      playerName
      companyName
      sport
    }
  }
`;

// Soft-delete one of the parent's PlayerContacts. Returns the deleted id so
// the client can evict it from the `parentPlayerContacts` list cache in place.
export const PARENT_DELETE_PLAYER_CONTACT = gql`
  mutation ParentDeletePlayerContact($_id: ID!) {
    parentDeletePlayerContact(_id: $_id)
  }
`;

// Takeover step 1 — OTP to the conflicting account's own address, which the
// client only ever holds in masked form, so the server resolves it by key.
export const PARENT_REQUEST_TAKEOVER_CODE = gql`
  mutation ParentRequestTakeoverCode($_id: ID!, $credentialKey: ID!) {
    parentRequestTakeoverCode(_id: $_id, credentialKey: $credentialKey)
  }
`;

// Takeover step 2 — moves the address and every player it gated onto this account.
export const PARENT_VERIFY_TAKEOVER = gql`
  mutation ParentVerifyTakeover($_id: ID!, $credentialKey: ID!, $code: String!) {
    parentVerifyTakeover(_id: $_id, credentialKey: $credentialKey, code: $code) {
      _id
      name
      emails
      phones
      token
      takeover {
        movedContacts
        retiredParent
      }
    }
  }
`;
